import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { Category } from "@insite/client-framework/Services/CategoryService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ValidateMSQ } from "@MasterBluePrint/Components/MsqValidation";
import loadInventoryForDataLayer from "@MasterBluePrint/Handlers/LoadInventoryForDataLayer";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@MasterBluePrint/Overrides/Components/SmallHeadingAndText";
import { FormattedCommaSeparatedValue } from "@MasterBluePrint/Services/FormattedValueService";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const category = getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value;
    return {
        category,
        currentPageUrl,
        wishListSettings: getSettingsCollection(state).wishListSettings,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    loadInventoryForDataLayer
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineCardGridHeadWrapper?: GridItemProps;
    orderLineCardGridHead?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    productImageLink: {
        css: css`
            img {
                border: 1px solid #ECECEC;
                border-radius: 2px;
            }
        `,
    },
    gridContainer: {
        gap: 10,
    },
    productInfoPartNumbersGridContainer: {
        css: css`
            p[data-test-selector="heading"] {
                color: ${themeColors.colorPrimary};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0;
                line-height: 20px;
                white-space: nowrap;
                text-transform: none;
                margin-right: 5px;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }

            p[data-test-selector="text"] {
                color: ${themeColors.colorCharcoalGrey};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0;
                line-height: 20px;
            }
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: .9375rem;
                padding-top: .9375rem;
                margin-bottom: .31250625rem;
            }
            padding-top: 1.875rem;
            margin-bottom: .5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },
    orderLineCardGridHeadWrapper: {
        width: 12,
        css: css`
            padding: 0.625rem 0.9375rem 0.625rem 0;
            border-top: 1px solid #C2D1D9 !important;
            margin: 0 0.625rem 0 0.625rem !important;
            border-top: 1px solid ${themeColors.colorBorderBrands};
            border-bottom: 1px solid ${themeColors.colorBorderBrands};

            .total-items {
                min-width: 300px;
            }
        `,
    },
    orderLineCardGridContainer: {
        css: css`
        .hide {
            display: none;
        }
        .show {
            display: block;
        }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            background: ${themeColors.colorWhite};

                            > div {
                                width: calc(100% + 10px) !important;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    orderLineCardGridHead: {
        css: css`
            display: block;
            width: 100%;

            h3 {
                color: ${themeColors.colorActiveMenuLink};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                font-weight: bold;
                letter-spacing: 0.5px;
                line-height: 24px;
            }

            .total-items {
                h3 {
                    color: ${themeColors.colorPrimary};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size16};
                    font-style: italic;
                    font-weight: 500;
                    letter-spacing: 0;
                }
            }

            > div {
                display: inline-block;
                text-align: left;
            }

            .sub-total {
                display: inline-block;
                text-align: right;
                float: right;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            h3, .price, .qty {
                                display: none;
                            }
                            .total-items {
                                h3 {
                                    display: block;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        css: css`
            flex-basis: auto;
            max-width: 120px;
            padding-left: 0.9375rem;
            @media print {
                //padding: 0;
            }
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 164px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        min-width: 120px;
                    `,
                    ],
                    "min",
                )}
            
        `,
    },
    orderLineCardImage: {
        css: css`
            width: 5.6575rem;
            border: 1px solid #ECECEC;
            border-radius: 2px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 10.25rem;
                            @media print{
                                width: 3.2rem;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [8, 8, 7, 9, 9],
        css: css`
            flex-basis: auto !important;
            //max-width: calc(100% - 234px) !important;
            padding-right: 0;

            &.mobile-content {
                display: none;
            }
            @media print {
                &.mobile-content {
                    display: none !important;
                }
            }            
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.desktop-content {
                                padding-left: 0;
                                flex-basis: calc(100% - 250px);
                                max-width: calc(100% - 250px);
                                min-width: calc(100% - 250px);
                                .orderLineOtherDetail {
                                    > div {
                                        .orderProductInfo {
                                            //min-width: 338px;
                                            min-width: 41%;
                                            @media print {
                                                min-width: 41%;
                                            }
                                        }
                                    }
                                }
                            }
                        `,
                    ],
                    "min",
                )}
            @media screen and (min-width:1300px) {
                &.desktop-content {
                    flex-basis: calc(100% - 250px);
                    max-width: calc(100% - 250px);
                    min-width: calc(100% - 250px);
                    .orderLineOtherDetail {
                        > div {
                            .orderProductInfo {
                                min-width: 41%;
                            }
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css` 
                            max-width: 100% !important;

                            &.mobile-content {
                                display: flex;
                                padding-bottom: 0;
                                width: 100%;

                                .product-info {
                                    display: none;
                                    @media print {
                                        display: none; 
                                    }
                                }
                            }

                            &.desktop-content {
                                padding-top: 0;
                                .product-description {
                                    display: none;
                                    @media print {
                                        display: none;
                                    }
                                }
                                .orderLineOtherDetail {
                                    > div {
                                        .orderProductInfo {
                                            border-top: 1px solid #E1E1E1;
                                            border-bottom: 1px solid #E1E1E1;
                                            padding: 1.875rem 0.3125rem;
                                            @media print {
                                                border:none;
                                            }
                                        }
                                        .product-options {
                                            margin-bottom: 0.9375rem;
                                        }
                                    }
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }  
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
        css: css`
            margin-bottom: 15px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                width: calc(100% + 10px) !important;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    orderLineCardAddToListGridItem: {
        css: css`
            align-items: center;
            flex-direction: column;
            flex-basis: auto;
            max-width: 130px;
            padding-left: 1.5625rem;
            padding-right: 0.9375rem;            

            .defaultadded {
                display: none;
            }

            .added-container {
                margin-top: 1.25rem;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        min-width: 128px;
                    `,
                    ],
                    "min",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        max-width: 100%;
                        padding-left: 0.9375rem;
                        .add-label {
                            display: inline-block;
                            position: relative;
                            top: -2px;
                            margin-left: 8px;
                        }

                        .icon-add {
                            display: inline-block;
                            position: relative;
                            top: 5px;                            
                        }
                    `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        .icon-add {
                            display: block;
                            margin: 0 auto;
                        }
                        .add-label {
                            display: block;
                        }
                    `,
                        null,
                        null,
                    ],
                    "min",
                )}			
        `,
        printWidth: 0,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`        
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        margin-top: 1.25rem !important;
                    `,
                    ],
                    "min",
                )}
        `,
    },
    productInfoGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 5,
        css: css`
            margin-bottom: 15px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
        css: css`
            //margin-bottom: 20px !important;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 0 !important;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
        css: css`
            span {
                color: ${themeColors.colorCharcoalGrey};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size14};
                letter-spacing: 0;
                line-height: 20px;
                text-transform: uppercase;
            }
        `,
    },
    productInfoDescriptionGridItem: {
        width: 12,
        css: css`
            button[data-test-selector="productDescriptionLink"],
            button[data-test-selector="productDescriptionLink"] > span {
                color: ${themeColors.colorPrimary};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size16};
                font-weight: 500;
                letter-spacing: 0;
                line-height: 20px;
            }
        `,
    },
    productInfoErpNumberGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;

            > div {
                flex-direction: row;

                > p[data-test-selector="heading"] {
                    &:after {
                        content: none !important;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 15px !important;

                            > div {
                                flex-direction: column;
                                color: ${themeColors.colorCharcoalGrey};
                                font-family: 'Lato',sans-serif;
                                font-size: ${themeFontSizes.size16};
                                letter-spacing: 0.5px;
                                line-height: 23px;

                                > p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorInfo};
                                    font-weight: bold;
                                    text-transform: capitalize;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoManufacturerItemGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            > div {
                flex-direction: row;
            }            

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                flex-direction: column;
                                color: ${themeColors.colorCharcoalGrey};
                                font-family: 'Lato',sans-serif;
                                font-size: ${themeFontSizes.size16};
                                letter-spacing: 0.5px;
                                line-height: 23px;

                                > p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorInfo};
                                    font-weight: bold;
                                    text-transform: capitalize;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoCustomerProductGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            > div {
                flex-direction: row;

                > p[data-test-selector="heading"] {
                    &:after {
                        content: none !important;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                flex-direction: column;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
        css: css`
            @media print{
                min-width: 20.625rem;
            }
            @media print and (orientation: landscape) {
                min-width: 34.375rem;
            }
            > div {
                width: calc(100% + 1px) !important;

            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                       padding-right: 9px;
                    `,
                    ],
                    "min",
                )}
        `,
    },
    orderLinePriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
            
            p[data-test-selector="heading"] {
                color: ${themeColors.colorInfo};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 5px;

                &:after {
                    content:":";
                    display:inline-block;
                }
                
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                        //min-width: 164px;
                        min-width: 33%;
                    `,
                    ],
                    "minMax",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        p[data-test-selector="heading"] {
                            display: none;
                        }
                    `,
                    ],
                    "min",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-top: 0.625rem;
                            padding-bottom: 0.625rem;
                            border-bottom: 1px solid #E1E1E1;
                            @media print{
                                border:none;
                            }
                        > div {
                            flex-direction: row;
                            
                            > p[data-test-selector="heading"] {
                                font-size: ${themeFontSizes.size16};
                                color: ${themeColors.colorInfo};
                                font-weight: bold;
                                text-transform: capitalize;
                            }
                        }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    vatLabelText: {
        size: 12,
        css: css`
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
        `,
    },
    secondaryVatLabelText: {
        size: 12,
        css: css`
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
        `,
    },
    productInfoQtyOrderedGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;

            p[data-test-selector="heading"] {
                color: ${themeColors.colorInfo};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 5px;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }
            

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            p[data-test-selector="heading"] {
                                display: none;
                            }
                            p[data-test-selector="text"] {
                                padding-left: 0;
                            }
                        `,
                        null,
                        null,
                    ],
                    "min",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`                        
                            border-bottom: 1px solid #E1E1E1;
                            padding-top: 0.625rem;
                            padding-bottom: 0.625rem;
                            @media print{
                                border:none;
                            }
                            > div {
                                flex-direction: row;
                                
                                > p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorInfo};
                                    font-weight: bold;
                                    text-transform: capitalize;
                                }
                            }
                            p[data-test-selector="text"] {
                                padding-left: 5px;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            align-items: flex-end;
            align-content: flex-end;
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            font-weight: bold;
            letter-spacing: 0.5px;
            line-height: 24px;
            text-align: right;

            p[data-test-selector="heading"] {
                color: ${themeColors.colorInfo};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 5px;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }

            > div {
                align-items: flex-end;
                align-content: flex-end;
                text-align: right;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            p[data-test-selector="heading"] {
                                display: none;
                            }

                            p[data-test-selector="text"] {
                                color: ${themeColors.colorCharcoalGrey};
                                font-weight: bold;
                                font-size: ${themeFontSizes.size16};
                            }
                        `,
                        null,
                        null,
                    ],
                    "min",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`                        
                            padding-top: 0.5rem;
                            padding-bottom: 0.625rem;
                            > div {
                                flex-direction: row;
                                align-items: flex-start;
                                align-content: flex-start;
                                text-align: left;

                                p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorInfo};
                                    font-weight: bold;
                                    text-transform: capitalize;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 8, 8, 8],
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoSubtotalHeadingAndText: {
        text: {
            weight: "bold",
        },
    },
    addToListClickable: {
        css: css`
            margin-top: 1.25rem;
            display: flex;
            flex-direction: column;
            

            .icon-add {
                svg {
                    width: 1.25rem;
                    height: 1.25rem;
                    stroke: ${themeColors.colorPrimary};
                }
            }
            .add-label {                
                color: ${themeColors.colorPrimary};
                font-family: 'Lato', sans-serif;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1.5rem;
                text-align: center;
                margin: 0 auto;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                        margin-top: 1.25rem;
                    `,
                        null,
                    ],
                    "min",
                )}
                
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        flex-direction: row;
                    `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}    
        `,
    },
};

const OrderLineProductInfo = ({ orderLine, styles }: { orderLine: OrderLineModel; styles: OrderLinesListStyles }) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    return (
        <GridItem {...styles.productInfoGridItem} className="orderProductInfo">
            <GridContainer {...styles.productInfoGridContainer}>
                <GridItem {...styles.productInfoBrandDescriptionGridItem} className="product-description">
                    <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                        {orderLine.brand && (
                            <GridItem {...styles.productInfoBrandGridItem}>
                                <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoDescriptionGridItem}>
                            <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {sectionOptions.length > 0 && (
                    <GridItem {...styles.productInfoSectionOptionsGridItem} className="product-options">
                        <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                    </GridItem>
                )}
                <GridItem {...styles.productInfoPartNumbersGridItem} className="product-info">
                    <GridContainer {...styles.productInfoPartNumbersGridContainer}>
                        <GridItem {...styles.productInfoErpNumberGridItem}>
                            <SmallHeadingAndText
                                {...styles.erpNumberHeadingAndText}
                                heading={translate("Part #")}
                                text={orderLine.productErpNumber}
                            />
                        </GridItem>
                        {orderLine.manufacturerItem && (
                            <GridItem {...styles.productInfoManufacturerItemGridItem}>
                                <SmallHeadingAndText
                                    {...styles.manufacturerHeadingAndText}
                                    heading={translate("MFG #")}
                                    text={orderLine.manufacturerItem}
                                />
                            </GridItem>
                        )}
                        {orderLine.customerProductNumber && (
                            <GridItem {...styles.productInfoCustomerProductGridItem}>
                                <SmallHeadingAndText
                                    {...styles.customerProductNumberHeadingAndText}
                                    heading={translate("My Part #")}
                                    text={orderLine.customerProductNumber}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
}) => {
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    return (
        <GridItem {...styles.orderLineInfoGridItem} id="priceContainer">
            <GridContainer {...styles.orderLineInfoGridContainer}>
                <GridItem {...styles.orderLinePriceGridItem} id="priceItem">
                    <SmallHeadingAndText
                        {...styles.priceHeadingAndText}
                        heading={translate("Price")}
                        text={
                            (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                ? orderLine.unitPriceWithVatDisplay
                                : orderLine.unitPriceDisplay) +
                            (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                        }
                    />
                    {enableVat && (
                        <>
                            <Typography as="p" {...styles.vatLabelText}>
                                {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                    ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                    : translate("Ex. VAT")}
                            </Typography>
                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <Typography {...styles.secondaryPriceText} as="p">
                                        {orderLine.unitPriceDisplay +
                                            (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                    </Typography>
                                    <Typography as="p" {...styles.secondaryVatLabelText}>
                                        {translate("Ex. VAT")}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </GridItem>

                <GridItem {...styles.productInfoTotalsGridItem}>
                    <GridContainer {...styles.productInfoTotalsGridContainer}>
                        <GridItem {...styles.productInfoQtyOrderedGridItem}>
                            <SmallHeadingAndText
                                {...styles.productInfoQtyOrderedHeadingAndText}
                                heading={translate("QTY")}
                                text={`${FormattedCommaSeparatedValue(orderLine.qtyOrdered)}`}
                            />
                        </GridItem>
                        <GridItem {...styles.productInfoSubtotalGridItem}>
                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <SmallHeadingAndText
                                    heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                    text={orderLine.extendedUnitNetPriceDisplay}
                                    extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                />
                            )}
                            <SmallHeadingAndText
                                heading={
                                    !enableVat
                                        ? translate("Subtotal")
                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                            ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                            : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                }
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? orderLine.netPriceWithVatDisplay
                                        : orderLine.extendedUnitNetPriceDisplay
                                }
                                extendedStyles={styles.productInfoSubtotalHeadingAndText}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {promotions.length > 0 && (
                    <GridItem {...styles.orderLineInfoPromotionGridItem}>
                        <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                    </GridItem>
                )}
            </GridContainer>
        </GridItem>
    );
};

const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
}) => {
    return orderLine.notes ? (
        <GridItem {...extendedStyles?.gridItemStyles}>
            <Typography {...extendedStyles?.typographyStyles}>{orderLine.notes}</Typography>
        </GridItem>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    styles: OrderLinesListStyles;
    category: Readonly<Category> | undefined;
    currentPageUrl: string;
    loadRealTimeInventoryForDataLayer: (parameter: {
            productId:string,
            listType: string,
            unitOfMeasure: string,
            product: OrderLineModel | CartLineModel | ProductContextModel,
            currentPageUrl: string,
            carouselTitle: string,
            category: Readonly<Category> | undefined,
            itemLinkClick: string | undefined,
    }) => void;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
}) => {
    const { orderLine, order, wishListSettings, category, currentPageUrl, styles, setAddToListModalIsOpen, loadRealTimeInventoryForDataLayer, addToWishList } = props;
    const [isAddToList, setAddedToList] = useState(false);
    const [isModelOpen, setModelOpen] = useState(false);

    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }
        const checkoutButton = document.querySelector('[data-test-selector="cartTotal_Checkout"]');
        const submitQuoteButton = document.querySelector('[data-test-selector="cartTotal_SubmitQuote"]');


        if (!ValidateMSQ(orderLine)) {
            const multipleSaleQty = orderLine?.properties?.multipleSaleQty
                ? Number(orderLine?.properties?.multipleSaleQty)
                : 0;
            checkoutButton?.setAttribute("disabled", "disabled");
            submitQuoteButton?.setAttribute("disabled", "disabled");
            toasterContext.addToast({
                body: siteMessage("Core_MultipleSaleQtyErrorSpire").toString().replace("{0}", `${multipleSaleQty}`),
                messageType: "danger",
            });
            return false;
        }
        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
        setModelOpen(true);
    };
    useEffect(() => {
        const lastUpdatedListId = getCookie("LastUpdatedListId");
        const lastupdatedProductId = getCookie("LastUpdatedProductId");
        if (lastUpdatedListId && lastupdatedProductId) {
            const submitQuoteButton = document.querySelector(`.visible-add_${lastupdatedProductId}`);
            submitQuoteButton?.classList.add('hide');
            const addedbtn = document.querySelector(`.added_${lastupdatedProductId}`);
            addedbtn?.classList.add('show');
        }

    }, [toasterContext]);

    const productClickDataLayerEvent = () => {
        if (orderLine.productId) {
            loadRealTimeInventoryForDataLayer({
                productId: orderLine.productId,
                listType: '',
                unitOfMeasure: orderLine.unitOfMeasure,
                product: orderLine,
                currentPageUrl,
                carouselTitle : '',
                category,
                itemLinkClick: 'Product Image',
            });
        }
    };

    return (
        <GridContainer
            {...styles.orderLineCardGridContainer}
            data-test-selector={`orderLineCard${orderLine.productId}`}
            className="order-line-card"
        >
            <GridItem {...styles.orderLineCardAddToListGridItem}>
                {orderLine.properties?.isProductAddedWishList === "true" ?
                    (
                        <Typography as="div" className="added-container">
                            <Icon src="Check" className="icon-add" />
                            <Typography as="span" className="add-label">{translate("Added to List")}</Typography>
                        </Typography>
                    ) :
                    <>
                        <Typography as="div" className={`added-container defaultadded added_${orderLine.productId}`}>
                            <Icon src="Check" className="icon-add" />
                            <Typography as="span" className="add-label">{translate("Added to List")}</Typography>
                        </Typography>

                        {orderLine.canAddToWishlist && (
                            <Link {...styles.addToListClickable} className={`visible-add_${orderLine.productId}`} onClick={addToListClickHandler}>
                                <Icon src="Plus" className="icon-add" />
                                <Typography as="span" className="add-label">{translate("Add to List")}</Typography>
                            </Link>
                        )}

                    </>
                }
            </GridItem>
            <GridItem {...styles.orderLineCardInfoGridItem} className="mobile-content">
                <GridContainer {...styles.orderLineCardInfoGridContainer} >
                    <OrderLineProductInfo orderLine={orderLine} styles={styles} />
                </GridContainer>
            </GridItem>
            <GridItem {...styles.orderLineCardImageGridItem}>
                {orderLine.productUri && orderLine.isActiveProduct && (
                    <Link {...styles.productImageLink} href={orderLine.productUri}
                    onClick={productClickDataLayerEvent}>
                        <LazyImage {...styles.orderLineCardImage} src={orderLine.mediumImagePath} />
                    </Link>
                )}
            </GridItem>
            <GridItem {...styles.orderLineCardInfoGridItem} className="desktop-content">
                <GridContainer {...styles.orderLineCardInfoGridContainer} className="orderLineOtherDetail">
                    <OrderLineProductInfo orderLine={orderLine} styles={styles} />
                    <OrderLineInfo orderLine={orderLine} order={order} styles={styles} />
                    <OrderLineNotes extendedStyles={styles.orderLineNotes} orderLine={orderLine} />
                </GridContainer>
            </GridItem>

        </GridContainer>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    loadInventoryForDataLayer,
    extendedStyles,
    currentPageUrl,
    category
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    if (!order || !order.orderLines) {
        return null;
    }

    const productsCount = order?.orderLines.length;
    const productsLabel = productsCount > 1 ? "Items in this Order" : "Item in this Order";



    function setWidthFromOffset() {
        setTimeout(() => {
            const elementPriceContainer = document.getElementById("priceContainer");
            const elementTotalItems = document.getElementById("totalItems");
            const elementPriceItem = document.getElementById("priceItem");
            const elementItemPriceHeading = document.getElementById("itemPrice");
            const elementOrderLineCard = document.querySelectorAll('.order-line-card')[0];

            if (elementPriceContainer && elementTotalItems && elementPriceItem && elementItemPriceHeading && elementOrderLineCard) {
                const leftOffsetNew = elementPriceContainer.getBoundingClientRect().left - elementOrderLineCard.getBoundingClientRect().left;
                const priceEleWidth = elementPriceItem.offsetWidth;
                elementTotalItems.style.width = `${leftOffsetNew + 15}px`;
                elementItemPriceHeading.style.width = `${priceEleWidth}px`;
            }
        }, 500);
    }

    if (document.body.clientWidth > 767) {
        setWidthFromOffset();
    }

    window.addEventListener("resize", setWidthFromOffset);

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.orderLineCardGridHeadWrapper}>
                <StyledWrapper {...styles.orderLineCardGridHead}>
                    <Typography id="totalItems" as="div" className="total-items"><Typography as="h3">{productsCount} {translate(productsLabel)}</Typography></Typography>
                    <Typography id="itemPrice" as="div" className="price"><Typography as="h3">{translate("Price")}</Typography></Typography>
                    <Typography id="itemQty" as="div" className="qty"><Typography as="h3">{translate("QTY")}</Typography></Typography>
                    <Typography as="div" className="sub-total"><Typography as="h3">{translate("SubTotal")}</Typography></Typography>
                </StyledWrapper>
            </GridItem>
            {order.orderLines.map(orderLine => orderLine.status !== "c" && (

                <CardContainer {...styles.orderLineCardContainer} key={orderLine.id}>
                    <GridItem {...styles.orderLineCardGridItem}>
                        <OrderLineCard
                            orderLine={orderLine}
                            order={order}
                            wishListSettings={wishListSettings}
                            styles={styles}
                            setAddToListModalIsOpen={setAddToListModalIsOpen}
                            loadRealTimeInventoryForDataLayer={loadInventoryForDataLayer}
                            addToWishList={addToWishList}
                            currentPageUrl={currentPageUrl}
                            category={category}
                        />
                    </GridItem>
                </CardContainer>
            ))}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
